import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { SampleReconciliation } from '@omni/classes/sample/sample-reconciliation.class';
import { MainCardViewDataModel } from '@omni/models/MainCardViewDataModel';
import { NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { SampleReconciliationService } from '@omni/services/sample/sample-reconciliation.service';
import { ReconciliationDetailComponent } from '../reconciliation-detail/reconciliation-detail.component';
import { UIService } from '@omni/services/ui/ui.service';
import { DeviceService } from '@omni/services/device/device.service';
import { Subscription } from 'rxjs';


@Component({
  selector: 'reconciliation-list',
  templateUrl: './reconciliation-list.component.html',
  styleUrl: './reconciliation-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class ReconciliationListComponent implements OnInit, OnDestroy {
  private sampleReconciliationRequests : SampleReconciliation[] = [];
  private filteredSampleReconciliationRequests : SampleReconciliation[] = [];
  public sampleReconciliationCards : MainCardViewDataModel[] = [];
  public displayedCards : MainCardViewDataModel[] = [];
  public searchText: string = '';
  public get hasFilteredResults(): boolean {
    return this.filteredSampleReconciliationRequests.length > 0;
  }
  public get filteredCount(): number {
    return this.filteredSampleReconciliationRequests.length;
  }
  private reconciliationRequestsSubscription: Subscription;
  private selectedRequestSubscription: Subscription;
  private readonly itemsPerPage: number = 20;
  private currentPage: number = 0;
  private hasMoreItems: boolean = true;
  private searchEventSubscription: Subscription;
  private offlineStatusSubscription: Subscription;
  public isOffline: boolean = false;

  constructor(
    private sampleReconciliationService : SampleReconciliationService,
    private navService: NavigationService,
    private uiService: UIService,
    private deviceService: DeviceService,
    private _cd: ChangeDetectorRef,) {}

  ngOnInit() {
    // Check offline status
    this.isOffline = this.deviceService.isOffline;
    
    // Subscribe to offline status changes
    this.offlineStatusSubscription = this.deviceService.isOfflineObservable.subscribe((isOffline: boolean) => {
      this.isOffline = isOffline;
      if (isOffline) {
        // Clear filtered requests when going offline to show count as 0
        this.filteredSampleReconciliationRequests = [];
      } else {
        // Restore filtered requests when coming back online
        this.filteredSampleReconciliationRequests = this.sampleReconciliationRequests;
        this.refreshCards();
      }
      this._cd.markForCheck();
    });

    this.loadReconciliationRequests();
    
    // Subscribe to updates
    this.reconciliationRequestsSubscription = this.sampleReconciliationService.reconciliationRequestsUpdated.subscribe((requests: SampleReconciliation[]) => {
      // this.sampleReconciliationRequests = requests;
      // Only update filtered requests if online, keep empty when offline
      if (this.isOffline) {
        this.filteredSampleReconciliationRequests = [];
      } else {
        this.filteredSampleReconciliationRequests = requests;
        this.sampleReconciliationRequests = requests;
      }
      this.refreshCards();
      this._cd.markForCheck();
    });

    // Subscribe to selected request changes to update selection state (no pagination reset)
    this.selectedRequestSubscription = this.sampleReconciliationService.selectedReconciliationRequest.subscribe(() => {
      this.updateSelectionState();
      this._cd.markForCheck();
    });

    // Subscribe to search event
    this.searchEventSubscription = this.sampleReconciliationService.searchEventSubject.subscribe((searchText: string) => {
      this.searchInput(searchText);
      this._cd.markForCheck();
    });
  }

  ngOnDestroy() {
    if (this.reconciliationRequestsSubscription) {
      this.reconciliationRequestsSubscription.unsubscribe();
    }
    if (this.selectedRequestSubscription) {
      this.selectedRequestSubscription.unsubscribe();
    }

    if (this.searchEventSubscription) {
      this.searchEventSubscription.unsubscribe();
    }

    if (this.offlineStatusSubscription) {
      this.offlineStatusSubscription.unsubscribe();
    }

    // Reset search when user leaves (tab change or navigate to another tool) so list shows full data on return
    this.sampleReconciliationService.searchEventSubject.next('');
  }

  private async loadReconciliationRequests() {
    this.sampleReconciliationRequests = this.sampleReconciliationService.sampleReconciliationRequests;
    
    // Clear filtered requests when offline to show count as 0
    if (this.isOffline) {
      this.filteredSampleReconciliationRequests = [];
    } else {
      this.filteredSampleReconciliationRequests = this.sampleReconciliationRequests;
      // Always fetch from server when loading the component (delta sync)
      await this.sampleReconciliationService.fetchReconciliationRequests(false, false);
    }
    
    this.refreshCards();
    this._cd.markForCheck();
  }

  private refreshCards() {
    if (this.isOffline) return;

    this.sampleReconciliationCards = [];
    if (this.filteredSampleReconciliationRequests.length > 0) {
      this.filteredSampleReconciliationRequests.forEach((sampleReconciliationRequest) => {
        this.sampleReconciliationCards.push(this.getViewDataModel(sampleReconciliationRequest));
      });
    }
    // Reset pagination when data is refreshed
    this.currentPage = 0;
    this.loadMoreItems();
  }

  /** Updates only which card is selected. Does not rebuild list or reset pagination. */
  private updateSelectionState() {
    const selectedId = this.sampleReconciliationService.selectedReconciliationRequestId;
    this.displayedCards.forEach((card) => {
      card.isSelected = card.id === selectedId;
    });
  }

  private loadMoreItems() {
    const startIndex = this.currentPage * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    const newItems = this.sampleReconciliationCards.slice(startIndex, endIndex);
    
    if (this.currentPage === 0) {
      this.displayedCards = newItems;
    } else {
      this.displayedCards = [...this.displayedCards, ...newItems];
    }
    
    this.hasMoreItems = endIndex < this.sampleReconciliationCards.length;
    this.currentPage++;
  }

  public doInfinite(event: any) {
    if (this.hasMoreItems) {
      this.loadMoreItems();
    }
    event.target.complete();
    
    // Disable infinite scroll if no more items
    if (!this.hasMoreItems) {
      event.target.disabled = true;
    }
  }

  public trackByCardId(index: number, card: MainCardViewDataModel): string {
    return card.id;
  }


  public getViewDataModel(sampleReconciliationRequest: SampleReconciliation) {
    let viewData: MainCardViewDataModel;
    viewData = {
      id: sampleReconciliationRequest.id,
      fixedHeight: true,
      primaryTextRight: sampleReconciliationRequest.name  ,
      subPrimaryTextRight: sampleReconciliationRequest._indskr_purpose_value_formatted,
      showArrow: false,
      showIcon: true,
      iconName: 'sample_reconciliation_icon',
      isSelected: this.sampleReconciliationService.selectedReconciliationRequestId === sampleReconciliationRequest.id,
      clickHandler: async (id: string, event, specificTarget) => {
        const isInCreateModeWithUnsavedChanges = !this.sampleReconciliationService.selectedReconciliationRequestId &&
          this.sampleReconciliationService.hasUnsavedCreateModeChanges;

        if (isInCreateModeWithUnsavedChanges) {
          const confirmed = await this.sampleReconciliationService.confirmDiscardCreateForm(true);
          if (!confirmed) {
            return;
          }
        }

        this.uiService.prevView = this.uiService.activeView;
        this.sampleReconciliationService.selectedReconciliationRequestId = id;
        this.sampleReconciliationService.selectedReconciliationRequest.next(this.sampleReconciliationRequests.find(request => request.id === id));
        this.uiService.activeView = 'sampleReconciliation';
        this.updateSelectionState();
      }
    };
    return viewData;
  }

  public searchInput(event: any) {
    const searchText = event || '';
    this.searchText = searchText;
    
    // Don't filter when offline, keep empty array
    if (this.isOffline) {
      this.filteredSampleReconciliationRequests = [];
    } else {
      this.filteredSampleReconciliationRequests = this.sampleReconciliationRequests.filter(request => request.name.toLowerCase().includes(searchText.toLowerCase()));
    }
    this.refreshCards();
  }
}
